import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

// Anything shaped like a translation key (TOASTS.SETTINGS_UPDATED,
// TOASTS.TITLES.DELETED, ...) is resolved through ngx-translate; anything else
// (API `detail` strings, already-translated text) is shown as-is.
const TRANSLATION_KEY = /^[A-Z0-9]+(?:_[A-Z0-9]+)*(?:\.[A-Z0-9]+(?:_[A-Z0-9]+)*)+$/;

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  constructor(
    private readonly _toastr: ToastrService,
    private readonly _translate: TranslateService
  ) {}

  public error(msg: any, params?: object, title: string = 'TOASTS.ERROR') {
    // Errors need reading (and often acting) time — don't let them vanish on
    // the short default timeout (WCAG P2-6).
    this._toastr.error(this.resolve(msg, params), this.resolve(title), {
      timeOut: 10000,
      extendedTimeOut: 5000
    });
  }

  public success(msg: string, params?: object, title: string = 'TOASTS.SUCCESS') {
    this._toastr.success(this.resolve(msg, params), this.resolve(title));
  }

  public warning(msg: string, params?: object, title: string = 'TOASTS.WARNING') {
    // Same reading-time budget as errors: a warning usually asks for a
    // follow-up action
    this._toastr.warning(this.resolve(msg, params), this.resolve(title), {
      timeOut: 10000,
      extendedTimeOut: 5000
    });
  }

  private resolve(value: any, params?: object): any {
    if (typeof value === 'string' && TRANSLATION_KEY.test(value)) {
      return this._translate.instant(value, this.resolveParams(params));
    }
    return value;
  }

  // Params may themselves be keys (e.g. `{ type: 'TYPES.REFERENCE' }`), so a
  // caller can compose a sentence from translated fragments without injecting
  // TranslateService.
  private resolveParams(params?: object): object | undefined {
    if (!params) {
      return params;
    }
    return Object.fromEntries(
      Object.entries(params).map(([k, v]) => [k, this.resolve(v)])
    );
  }
}
